import React, { useMemo } from "react"
import { useShallow } from "zustand/react/shallow"
import { differenceWith, isEqual } from "lodash"

// components
// import RJLabel from "../../components/RJLabel/RJLabel"
import LabelMenu from "../../components/LabelMenu/LabelMenu"
import RJReadOnly from "../../components/RJReadOnly/RJReadOnly"
import TranslationLabel from "../../components/TranslationLabel/TranslationLabel"
import { Skeleton } from "antd"
import Note from "components/Note/Note"
import CharCounter from "components/CharCounter/CharCounter"

// utils/helpers
import { registryValidMsgs } from "utilities/registryValidations"
import { useInView } from "react-intersection-observer"
import { getRegistryKeys } from "../../../utils"
import { formSchemas } from "constants/authoring"

// context
import useHOC from "../useHOC/useHOC"
import useAuthoringViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringViewStore"
import useAuthoringPrintStore from "../../../hooks/stores/useAuthoringPrintStore"
import useAuthoringDataStore from "../../../hooks/stores/useAuthoringDataStore"
import useAuthoringFieldStore from "../../../hooks/stores/useAuthoringFieldStore"
import useAuthoringTranslations from "../../../hooks/stores/useAuthoringTranslations"
import useAuthoringValidationsStore from "../../../hooks/stores/useAuthoringValidationsStore"

const InputHOC = (props) => {
  const { WrappedComponent, formatOptions } = props

  const jpath = props.id?.split(".").join("_")

  const { hiddenFields, lockedFields } = useAuthoringFieldStore(
    useShallow((state) => ({
      hiddenFields: state.hiddenFields,
      lockedFields: state.lockedFields,
    }))
  )

  const { forkedStudyData, overrideData, translationsData } = useAuthoringDataStore(
    useShallow((state) => ({
      forkedStudyData: state.forkedStudyData,
      overrideData: state.overrideData,
      translationsData: state.translationsData,
    }))
  )

  const { formView, formSection, readOnly } = useAuthoringViewStore(
    useShallow((state) => ({
      formView: state.formView,
      formSection: state.formSection,
      readOnly: state.readOnly,
    }))
  )

  const { isTranslationActive, selectedLanguage } = useAuthoringTranslations(
    useShallow((state) => ({
      selectedLanguage: state.selectedLanguage,
      isTranslationActive: state.isTranslationActive,
    }))
  )

  const { registryValidations } = useAuthoringValidationsStore(
    useShallow((state) => ({
      registryValidations: state.registryValidations,
    }))
  )

  const uiOptions = props.uiSchema["ui:options"]

  const registryKeys = useMemo(() => {
    const regKeys = getRegistryKeys({
      formSection,
      formSchemas,
      hiddenFields,
      jpath,
    })

    return regKeys || []
  }, [formSection, hiddenFields])

  const hoc = useHOC({
    WrappedComponent,
    props,
    formatOptions,
    jpath,
    registryKeys,
  })

  const {
    showMenu,
    setShowMenu,
    options,
    fieldComments,
    isFieldLocked,
    displayLockedField,
    invalid,
    invalidText,
    fieldLoading,
    setFieldLoading,
    isDisabled,
    jpathDotNotation,
    value,
    onChange,
    disableValidation,
    forkedFormViewId,
  } = hoc

  const forkedRegistryKeys = registryKeys.filter((key) => forkedStudyData?.[key]?.hasOwnProperty(jpathDotNotation))

  const filteredRegistrykeys = useMemo(() => {
    const diff = differenceWith(registryKeys, forkedRegistryKeys, isEqual)
    return diff
  }, [forkedRegistryKeys, registryKeys])

  const { isPrinting } = useAuthoringPrintStore(
    useShallow((state) => ({
      isPrinting: state.isPrinting,
    }))
  )

  const { ref: visibleRef, inView: inViewport } = useInView({
    /* Optional options */
    threshold: 0,
    triggerOnce: true,
  })

  let classNames = ["authoring-input-wrapper-container rjs-field"]
  const containerClassNames = []
  if (options?.classNames?.field) classNames.push(options.classNames.field)
  if (isTranslationActive) {
    containerClassNames.push("translate-active")
    classNames.push("rjs-full")
  }

  const renderField = ({ isForked, forkedKey }) => {
    let path = jpath
    let val = value
    let regKeys = filteredRegistrykeys
    let forkedReg = forkedKey === "eudra" ? "eudract" : forkedKey

    if (isForked) {
      path = `${jpath}_${forkedKey}`
      val = forkedStudyData?.[forkedReg]?.[jpathDotNotation]
      regKeys = [forkedKey]
    }

    return (
      <div
        className="flex-1"
        key={path}
      >
        {options.removeLabel !== true && (
          <LabelMenu
            fieldComments={fieldComments}
            registryKeys={regKeys}
            forkedRegistryKey={forkedKey}
            isForked={isForked}
            hideFork={uiOptions?.hideFork}
            showMenu={showMenu}
            setShowMenu={setShowMenu}
            jpath={path}
            setFieldLoading={setFieldLoading}
            formContext={props.formContext}
            jpathDotNotation={jpathDotNotation}
            isLocked={isFieldLocked}
            value={val}
            forkedFormViewId={forkedFormViewId}
            {...options}
          />
        )}
        <div className={classNames.join(" ")}>
          {(inViewport || isPrinting) && (
            <>
              <div style={{ position: "relative" }}>
                {readOnly ? (
                  <>
                    <RJReadOnly
                      {...props}
                      id={`${path}-read-only`}
                      formView={formView}
                      carbonProps={options}
                      isForked={isForked}
                      value={val}
                      jpathDotNotation={jpathDotNotation}
                      overrideJpath={lockedFields?.[path]}
                      forkedRegistryKey={forkedKey}
                    />
                    <div className="input-wrapper-bottom-helper-container">
                      <div className="input-wrapper-bottom-helper-left">
                        {registryValidMsgs(registryValidations, jpath, invalid)}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <WrappedComponent
                      {...props}
                      path={path}
                      carbonProps={{
                        ...options,
                        disabled: isDisabled && !isForked,
                      }}
                      invalid={disableValidation === false && invalid}
                      invalidText={disableValidation === false && invalidText}
                      // onFocus={handleOnFocus}
                      // onBlur={handleOnBlur}
                      value={val}
                      onChange={(val, option, fieldType) =>
                        onChange(val, option, false, fieldType, isForked, forkedKey)
                      }
                    />
                    <div className="input-wrapper-bottom-helper-container">
                      <div className="input-wrapper-bottom-helper-left">
                        {disableValidation === false && invalid && (
                          <Note
                            type="error"
                            text={invalidText}
                          />
                        )}
                        {registryValidMsgs(registryValidations, jpath, invalid)}
                      </div>
                      <div className="input-wrapper-bottom-helper-right">
                        <div className="revert-override-container">
                          {options?.disabled !== true && !isForked && displayLockedField(isFieldLocked, path)}
                          {overrideData?.[lockedFields?.[path]] !== undefined && !isForked && (
                            <>
                              <i className="fad fa-check-circle"></i>
                              Override applied
                            </>
                          )}
                        </div>
                        {options.charCountEnabled && formView.id !== "global" && (
                          <CharCounter
                            maxChars={options?.maxChars}
                            value={val}
                          />
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    )
  }

  if (fieldLoading) {
    return (
      <div className={containerClassNames}>
        <Skeleton.Input active={true} />
      </div>
    )
  }

  return (
    <div
      id={jpath}
      ref={visibleRef}
      className={containerClassNames}
      onMouseEnter={() => setShowMenu(true)}
      onMouseLeave={() => setShowMenu(false)}
    >
      <div className="flex-1">
        {renderField({
          isForked: forkedRegistryKeys.findIndex((regKey) => regKey === forkedFormViewId) === -1 ? false : true,
          forkedKey: forkedRegistryKeys.find((regKey) => regKey === forkedFormViewId),
        })}
      </div>
      {forkedFormViewId === "global" &&
        forkedRegistryKeys?.length > 0 &&
        forkedRegistryKeys.map((regKey) => {
          return (
            <div
              key={regKey}
              className="flex-1"
              style={{ marginTop: 6 }}
            >
              {renderField({ isForked: true, forkedKey: regKey })}
            </div>
          )
        })}

      {isTranslationActive && (
        <div className="flex-1">
          {(inViewport || isPrinting) && isTranslationActive && selectedLanguage && (
            <div className="translation-wrapped-component">
              <TranslationLabel
                {...options}
                selectedLanguage={selectedLanguage}
              />
              {readOnly ? (
                <>
                  <RJReadOnly
                    {...props}
                    id={jpath}
                    jpathDotNotation={jpathDotNotation}
                    formView={formView}
                    carbonProps={options}
                    isTranslation={true}
                    value={translationsData?.[selectedLanguage]?.[jpathDotNotation]}
                    overrideJpath={lockedFields?.[jpath]}
                    // forkedRegistryKey={undefined}
                  />
                </>
              ) : (
                <WrappedComponent
                  {...props}
                  carbonProps={{
                    ...options,
                    disabled: options.disabled || props.disabled,
                  }}
                  isTranslation={true}
                  originalValue={value}
                  selectedLanguage={selectedLanguage}
                  id={`${jpath}-${selectedLanguage}`}
                  onChange={(val, option, fieldType) => onChange(val, option, true, fieldType)}
                  value={translationsData?.[selectedLanguage]?.[jpathDotNotation]}
                />
              )}
              <div className="input-wrapper-bottom-helper-container">
                <div className="input-wrapper-bottom-helper-left"></div>
                <div className="input-wrapper-bottom-helper-right">
                  {options.charCountEnabled &&
                    options?.translations?.[selectedLanguage]?.charCountEnabled !== false &&
                    formView.id !== "global" &&
                    readOnly !== true && (
                      <CharCounter
                        maxChars={options?.translations?.[selectedLanguage]?.maxChars || options?.maxChars}
                        value={translationsData?.[selectedLanguage]?.[jpathDotNotation]}
                      />
                    )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default InputHOC
